// @ts-strict-ignore
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  useIsPlaylistOnListLazyQuery,
  useToggleFavoritePlaylistMutation
} from 'api';
import { host } from 'f';
import { getSignInUrl, useAuthenticated } from 'lib/auth';
import { useDidMount } from 'lib/hooks/useDidMount';
import { createLogger } from 'lib/logging';

import { BaseButtonType } from 'components/base-button';
import IconCTA from 'components/icon-cta';
import { Text } from 'components/typography';

import AddToListIcon from 'icons/AddToListIcon';
import RemoveFromListIcon from 'icons/RemoveFromListIcon';

type AddPlaylistToListProps = {
  playlistId: string;
  playlistSlug: string;
};

type ListStates = 'inList' | 'adding' | 'notInList' | 'removing';

const log = createLogger('Add Playlist To List');

export default function AddPlaylistToList({
  playlistId,
  playlistSlug
}: AddPlaylistToListProps): React.ReactNode {
  const loggedIn = useAuthenticated();
  const isMounted = useDidMount();

  const intl = useIntl();
  const addToListText = intl.formatMessage({
    defaultMessage: 'Add to list'
  });
  const addingToListText = intl.formatMessage({
    defaultMessage: 'Adding...'
  });
  const removeFromListText = intl.formatMessage({
    defaultMessage: 'Remove from list'
  });
  const removingFromListText = intl.formatMessage({
    defaultMessage: 'Removing...'
  });
  const errorMessage = intl.formatMessage({
    defaultMessage: 'Please try again later.'
  });

  const [listState, setListState] = useState<ListStates>();

  const [isPlaylistOnListLazyQuery, { loading, error, data, refetch }] =
    useIsPlaylistOnListLazyQuery({
      variables: { id: playlistId },
      ssr: false,
      fetchPolicy: 'cache-and-network'
    });

  const isLoading = loading && isMounted;
  const isLoggedIn = loggedIn && isMounted;

  useEffect(() => {
    if (playlistId && !isLoading && isLoggedIn) {
      isPlaylistOnListLazyQuery();
    }
  }, [isLoggedIn, data, playlistId]);

  const [
    toggleSavedPlaylist,
    { loading: mutationLoading, error: mutationError }
  ] = useToggleFavoritePlaylistMutation({
    variables: {
      id: playlistId,
      shouldSaveForLater: listState === 'notInList'
    }
  });

  useEffect(() => {
    if (!isLoggedIn || isLoading) return;

    if (data) {
      setListState(data?.yourListItem?.id ? 'inList' : 'notInList');
    }
  }, [data, isLoading, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || isLoading) return;
    if (mutationLoading) {
      if (listState === 'inList') {
        setListState('removing');
      }
      if (listState === 'notInList') {
        setListState('adding');
      }
    } else if (refetch) {
      refetch();
    }
  }, [mutationLoading]);

  useEffect(() => {
    if (!isLoggedIn || isLoading) return;
    if (error || mutationError) {
      setListState(listState === 'removing' ? 'inList' : 'notInList');
    }
  }, [error, mutationError]);

  const getIcon = () => {
    switch (listState) {
      case 'inList':
      case 'removing':
        return <RemoveFromListIcon className="h-6 w-6" />;
      case 'adding':
      case 'notInList':
      default:
        return <AddToListIcon variant="add" className="h-6 w-6" />;
    }
  };

  const getButtonText = () => {
    if (isLoading) {
      return '';
    }
    switch (listState) {
      case 'inList':
        return removeFromListText;
      case 'adding':
        return addingToListText;
      case 'notInList':
        return addToListText;
      case 'removing':
        return removingFromListText;
      default:
        return addToListText;
    }
  };

  const toggleClick = () => {
    try {
      toggleSavedPlaylist();
    } catch {
      log.error('adding/removing playlist to watch later list failed', error);
    }
  };

  const showLoader =
    listState === 'adding' ||
    listState === 'removing' ||
    isLoading ||
    mutationLoading;

  const dataError = error || mutationError;

  return (
    <div className="relative mb-2 mr-4 md:mb-0 md:mr-6">
      <IconCTA
        icon={getIcon()}
        text={getButtonText()}
        link={
          isLoggedIn
            ? null
            : getSignInUrl({
                context: 'ted.www/watch-later',
                referrer: `${host}/playlists/${playlistId}/${playlistSlug}`
              })
        }
        onClick={isLoggedIn ? () => toggleClick() : null}
        isLoading={showLoader}
        type={BaseButtonType.Tertiary}
        disabled={!!dataError}
      />
      {dataError && (
        <Text size="s" className="absolute top-full w-max text-red-700">
          {errorMessage}
        </Text>
      )}
    </div>
  );
}
